import { useMemo, useEffect } from 'react'
import { useLocation } from 'react-router'
import { toast } from 'react-toastify'

import { SurveyForm } from '@/components/SurveyForm'
import { SurveyErrorState } from '@/components/SurveyErrorState'
import { Answer, PreviewJD } from '@/interfaces/answer'
import { useGetSurveys } from '@/hooks/queries/useSurveys'
import { SurveyType, SurveyQuestionType } from '@/interfaces/survey'
import { LoadingScreen } from '@/components/loading-screen'
import { useSubmitAnswers } from '@/hooks/mutations/useSubmitAnswers'
import { useLoadingStore } from '@/store/loadingStore'
import { useGetUserById } from '@/hooks/queries/useGetUserById'
import { handleError } from '@/utils/helpers'

const SendJDPage = () => {
  const location = useLocation()
  const { targetUserId } =
    (location.state as { targetUserId?: string; targetUserName?: string }) || {}

  const { data: survey, isLoading, error } = useGetSurveys(SurveyType.SEND_JD)
  const { mutate: submitAnswers } = useSubmitAnswers()
  const { showLoading, hideLoading } = useLoadingStore()

  // Fetch user by ID from state
  const {
    data: targetUser,
    error: userError,
    isLoading: isUserLoading,
  } = useGetUserById(targetUserId || null)

  // Show error toast if user not found
  useEffect(() => {
    if (userError && targetUserId) {
      toast.error('Không tìm thấy người dùng')
    }
  }, [userError, targetUserId])

  // Sort questions and find the first SEARCH type question for personNameQuestionId
  const sortedQuestions = useMemo(() => {
    if (!survey || !survey.questions) return []
    return survey.questions.sort((a, b) => a.order - b.order)
  }, [survey])

  const personNameQuestionId = useMemo(() => {
    return sortedQuestions.find((q) => q.type === SurveyQuestionType.SEARCH)?.id
  }, [sortedQuestions])

  const handleSubmit = (
    payload: { surveyId: string; answers: Answer[]; targetUserId?: string },
    onSuccess: (answerId: string, previewJD: PreviewJD) => void
  ) => {
    showLoading('Đang gửi Untold JD...')
    submitAnswers(payload, {
      onSuccess: (data) => {
        hideLoading()
        console.log('Send JD completed!', {
          surveyId: payload.surveyId,
          answers: payload.answers,
          targetUserId: payload.targetUserId,
          answerId: data.id,
          previewJD: data.previewJD,
        })
        // Call the onSuccess callback with answerId and previewJD
        onSuccess(data.id, data.previewJD)
      },
      onError: (error) => {
        hideLoading()
        handleError(error, { fallbackMessage: 'Đã xảy ra lỗi khi gửi Untold JD' })
      },
    })
  }

  // Loading state - wait for both survey and user (if targetUserId is provided)
  if (isLoading || (targetUserId && isUserLoading)) {
    return <LoadingScreen message="Đang tải câu hỏi..." />
  }

  // Error state
  if (error) {
    return <SurveyErrorState title="Lỗi khi tải câu hỏi" message={error.message} />
  }

  // No questions available
  if (!survey || sortedQuestions.length === 0) {
    return (
      <SurveyErrorState
        title="Không có câu hỏi nào"
        message="Hiện tại không có câu hỏi nào. Vui lòng thử lại sau."
      />
    )
  }

  return (
    <SurveyForm
      key={`${location.key}-${targetUserId || 'no-user'}`} // Force re-render when navigating with different targetUserId
      title="Có đồng nghiệp bạn thật sự nể, muốn gửi lời khen nhưng thân quá nên ngại nói.\nĐây là dịp để nói ra để cổ vũ họ. Phiên bản Untold JD này có thể khiến đồng nghiệp cười cả ngày vì đôi mắt tinh tường của bạn"
      highlightedText="Đây là dịp để nói ra để cổ vũ họ"
      questions={sortedQuestions}
      onSubmit={handleSubmit}
      personNameQuestionId={personNameQuestionId}
      surveyId={survey?.id || ''}
      initialSelectedUser={targetUser}
    />
  )
}

export default SendJDPage
